import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { trackBookDownload } from "../lib/trackBookDownload";
import { Download, BookOpen, ArrowRight, Play, Pause, Headphones } from "lucide-react";
import { CHAPTERS_SUMMARY } from "../lib/content";
import { BookReader } from "./BookReader";
import { AudiobookPlayer } from "./AudiobookPlayer";

const BOOK_IMG =
  "https://customer-assets.emergentagent.com/job_ya-digital/artifacts/ksa7ujmw_Libro%20para%20libro.png";

const BOOK_PDF =
  "https://customer-assets.emergentagent.com/job_ya-digital/artifacts/riplp2t7_Resumen_por_capitulos_Capitalismo_de_Abundancia.pdf";

const BOOK_PDF_FULL =
  "https://customer-assets.emergentagent.com/job_ya-digital/artifacts/ih361ij3_CAPITALISMO%20DE%20ABUNDANCIA%20-%20LIBRO%20200%20HOJAS.pdf";

export const BookSection = () => {
  const [readerOpen, setReaderOpen] = useState(false);
  const [initialPage, setInitialPage] = useState(null);
  const [audioOpen, setAudioOpen] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState({ cur: 0, dur: 0 });
  const audioRef = useRef(null);

  const toggleResumenAudio = () => {
    if (!audioOpen) {
      setAudioOpen(true);
      // Reproducir tras montar el <audio>
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
          setAudioPlaying(true);
        }
      }, 100);
      return;
    }
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play();
      setAudioPlaying(true);
    } else {
      audioRef.current.pause();
      setAudioPlaying(false);
    }
  };

  const fmtTime = (s) => {
    if (!Number.isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const openReader = (page = null) => {
    setInitialPage(page);
    setReaderOpen(true);
  };

  // Listen for "openBookReader" custom events from anywhere on the page
  useEffect(() => {
    const handler = (e) => {
      const page = e.detail?.page ?? null;
      // Scroll a la sección libro y abrir el lector
      const el = document.getElementById("libro");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Pequeño delay para que primero termine el scroll
      setTimeout(() => openReader(page), 350);
    };
    window.addEventListener("openBookReader", handler);
    return () => window.removeEventListener("openBookReader", handler);
  }, []);

  return (
    <>
      <section id="libro" className="bg-white py-24 md:py-32" data-testid="book-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-12">
            {/* Image (clickeable → abre lector) */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5"
            >
              <button
                onClick={() => openReader()}
                className="group relative block w-full focus:outline-none"
                data-testid="book-image-btn"
                aria-label="Resumen del libro interactivo"
              >
                <div className="absolute -inset-6 bg-[#F5B400]/15 -z-10 group-hover:bg-[#F5B400]/25 transition-colors" />
                <img
                  src={BOOK_IMG}
                  alt="Capitalismo de Abundancia — libro"
                  className="w-full max-w-md mx-auto drop-shadow-2xl group-hover:scale-[1.02] transition-transform duration-500"
                  data-testid="book-image"
                />
                {/* Overlay botón LEER LIBRO con latido constante */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="relative">
                    {/* Pulse ring (latido) */}
                    <span
                      aria-hidden
                      className="absolute inset-0 bg-[#F5B400] -z-10 animate-ping"
                      style={{ animationDuration: "1.6s" }}
                    />
                    <div className="relative bg-[#F5B400] text-[#0A192F] px-5 py-3 font-headings uppercase tracking-wide text-sm shadow-2xl inline-flex items-center gap-2 fy-heartbeat">
                      <BookOpen size={16} /> Resumen del libro
                    </div>
                  </div>
                </div>
              </button>
              <p className="text-center text-xs text-[#9CA3AF] mt-4 uppercase tracking-[0.2em] font-headings">
                Tocá el libro para hojearlo
              </p>

              {/* Botón Escuchar el Resumen + descargar + player inline */}
              <div className="mt-6">
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={toggleResumenAudio}
                    className="group flex-1 bg-[#0A192F] hover:bg-[#0A2463] text-[#F5B400] font-headings uppercase tracking-[0.18em] text-sm px-6 py-4 inline-flex items-center justify-center gap-3 transition-all border-2 border-[#F5B400]"
                    data-testid="book-listen-resumen-btn"
                    aria-label="Escuchar el resumen del libro"
                  >
                    {audioOpen && audioPlaying ? (
                      <Pause size={18} fill="currentColor" />
                    ) : audioOpen ? (
                      <Play size={18} fill="currentColor" />
                    ) : (
                      <Headphones size={18} />
                    )}
                    <span>
                      {audioOpen && audioPlaying
                        ? "Pausar resumen"
                        : audioOpen
                        ? "Continuar resumen"
                        : "Escuchar el Resumen"}
                    </span>
                  </button>
                  <a
                    href="/audio/resumen.mp3"
                    download="Capitalismo-de-Abundancia-Resumen.mp3"
                    className="sm:w-auto bg-transparent hover:bg-[#F5B400] hover:text-[#0A192F] text-[#0A192F] font-headings uppercase tracking-[0.18em] text-sm px-6 py-4 inline-flex items-center justify-center gap-3 transition-all border-2 border-[#0A192F]"
                    data-testid="book-download-audio-btn"
                    aria-label="Descargar audio del resumen"
                  >
                    <Download size={18} />
                    <span className="sm:hidden">Descargar audio</span>
                    <span className="hidden sm:inline">Descargar audio</span>
                  </a>
                </div>

                {audioOpen && (
                  <div className="mt-3 bg-[#0A192F] border border-[#F5B400]/20 p-4" data-testid="resumen-audio-panel">
                    <audio
                      ref={audioRef}
                      src="/audio/resumen.mp3"
                      preload="metadata"
                      onLoadedMetadata={(e) =>
                        setAudioProgress({ cur: 0, dur: e.currentTarget.duration || 0 })
                      }
                      onTimeUpdate={(e) =>
                        setAudioProgress({
                          cur: e.currentTarget.currentTime,
                          dur: e.currentTarget.duration || 0,
                        })
                      }
                      onPlay={() => setAudioPlaying(true)}
                      onPause={() => setAudioPlaying(false)}
                      onEnded={() => setAudioPlaying(false)}
                      data-testid="resumen-audio-element"
                    />
                    {/* Barra de progreso */}
                    <input
                      type="range"
                      min="0"
                      max={audioProgress.dur || 0}
                      step="0.1"
                      value={audioProgress.cur}
                      onChange={(e) => {
                        if (audioRef.current) {
                          audioRef.current.currentTime = parseFloat(e.target.value);
                        }
                      }}
                      className="w-full accent-[#F5B400] cursor-pointer"
                      data-testid="resumen-audio-progress"
                    />
                    <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] font-headings text-[#9CA3AF] mt-2">
                      <span data-testid="resumen-audio-current">{fmtTime(audioProgress.cur)}</span>
                      <span className="text-[#F5B400]">
                        Resumen narrado · ~{fmtTime(audioProgress.dur)}
                      </span>
                      <span data-testid="resumen-audio-total">
                        -{fmtTime((audioProgress.dur || 0) - audioProgress.cur)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Text */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#0A2463]" />
                <span className="font-headings text-xs tracking-[0.3em] uppercase text-[#0A2463]">
                  El libro · Doctrina completa
                </span>
              </div>
              <h2 className="font-headings font-bold uppercase tracking-tight text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                Capitalismo<br />
                <span className="fy-underline">de Abundancia</span>
              </h2>
              <p className="mt-3 text-base text-[#4B5563] tracking-wide">
                Claudio Alejandro Nazar · 30 capítulos · 200 páginas · 6 partes
              </p>
              <p className="mt-6 text-lg text-[#4B5563] leading-relaxed max-w-2xl">
                Una nueva doctrina nacional para Argentina. Patrimonio universal, soberanía operativa,
                industrialización con valor agregado y Estado arquitecto. El plan completo para pasar de la
                escasez producida institucionalmente a la abundancia construida.
              </p>

              {/* Parts */}
              <div className="mt-10 grid sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200" data-testid="book-parts">
                {CHAPTERS_SUMMARY.map((c) => (
                  <button
                    key={c.parte}
                    onClick={() => openReader(c.page)}
                    className="bg-white p-5 text-left hover:bg-gray-50 transition-colors group"
                    data-testid={`book-part-${c.parte}`}
                    title={`Abrir libro en la página ${c.page}`}
                  >
                    <div className="flex items-baseline gap-3 mb-1.5">
                      <span className="font-headings font-bold text-[#F5B400] tabular-nums text-2xl">
                        {c.parte}
                      </span>
                      <h4 className="font-headings text-sm uppercase tracking-wider text-[#0A192F] leading-tight">
                        {c.titulo}
                      </h4>
                    </div>
                    <p className="text-sm text-[#4B5563]">{c.desc}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] mt-2 font-headings group-hover:text-[#0A2463] transition-colors">
                      Página {c.page} →
                    </p>
                  </button>
                ))}
              </div>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => openReader()}
                  className="group bg-[#0A2463] hover:bg-[#081b4b] text-white font-headings uppercase tracking-wide text-base px-8 py-4 inline-flex items-center justify-center gap-2 transition-all"
                  data-testid="book-read-btn"
                >
                  <Play size={16} fill="currentColor" /> Leer interactivo
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  href={BOOK_PDF_FULL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackBookDownload("book-section-full")}
                  className="group border-2 border-[#0A192F] hover:bg-[#0A192F] hover:text-white text-[#0A192F] font-headings uppercase tracking-wide text-base px-8 py-4 inline-flex items-center justify-center gap-2 transition-all"
                  data-testid="book-download-btn"
                >
                  <Download size={18} /> Descargar libro completo
                </a>
              </div>
            </div>
          </div>

          {/* Audiolibro */}
          <AudiobookPlayer />
        </div>
      </section>

      {/* Lector modal flipbook */}
      <BookReader
        open={readerOpen}
        onClose={() => setReaderOpen(false)}
        initialPage={initialPage}
      />
    </>
  );
};

export default BookSection;
